<app-header></app-header>
    
    <main class="container mx-auto px-4 pb-6 lg:px-6 lg:py-8">
        <!-- Hero Section -->
        <section class="mt-4 mb-8 lg:mb-12">
            <div class="bg-gradient-to-r from-wattpad-primary to-wattpad-secondary rounded-xl p-6 lg:p-8 text-white">
                <div class="flex flex-col lg:flex-row gap-6 items-center">
                    <div class="lg:w-1/3 flex justify-center">
                        <div class="bg-white/20 p-6 rounded-full w-48 h-48 flex items-center justify-center">
                            <i class="fas fa-hand-holding-usd text-6xl"></i>
                        </div>
                    </div>
                    <div class="lg:w-2/3 text-center lg:text-left">
                        <h1 class="text-2xl lg:text-4xl font-bold mb-2">💰 Refund & Cancellation Policy</h1>
                        <p class="text-lg lg:text-2xl mb-4">Last Updated: 28th October, 2025</p>
                        
                        <p class="text-sm lg:text-base mb-6">
                            Welcome to Funtel Stories. We value your time, trust, and financial commitment. 
                            This Refund & Cancellation Policy explains how refunds, returns, and cancellations 
                            are handled for digital content, subscriptions, and purchases made on our platform.
                        </p>
                        
                        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                            <a href="#digital-content" class="bg-white text-wattpad-primary px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors text-lg">
                                Digital Content Policy
                            </a>
                            <a href="#contact" class="bg-white/20 text-white px-8 py-3 rounded-full font-medium hover:bg-white/30 transition-colors text-lg">
                                Contact Support
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Important Notice -->
        <section class="mb-8 lg:mb-12" id="important-notice">
            <div class="bg-wattpad-accent/10 border border-wattpad-accent/20 rounded-xl p-6">
                <div class="flex items-start">
                    <i class="fas fa-exclamation-triangle text-wattpad-accent text-xl mt-1 mr-4"></i>
                    <div>
                        <h3 class="font-bold text-lg mb-2">👉 Important Notice</h3>
                        <p class="text-wattpad-dark dark:text-gray-300 mb-2">
                            All purchases of digital content, subscriptions, and virtual currency on Funtel Stories are final and non-refundable, except in specific circumstances outlined in this policy.
                        </p>
                        <p class="text-wattpad-dark dark:text-gray-300">
                            By making any purchase on our platform, you acknowledge and agree to the terms of this Refund & Cancellation Policy.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Refund & Cancellation Policy Content -->
        <section class="mb-8 lg:mb-12">
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                <!-- Digital Content Section -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50" id="digital-content">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">1. Digital Content – No Physical Delivery</h2>
                    <p class="mb-4">
                        All stories, audiobooks, and digital products on Funtel Stories are delivered electronically.
                    </p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>There is no physical shipping, so no return of goods is applicable.</li>
                        <li>Once digital content is accessed or downloaded, it cannot be "returned" in the traditional sense.</li>
                    </ul>
                </div>

                <!-- Refunds Section -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">2. Refunds</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">2.1 Non-Refundable Purchases</h3>
                    <p class="mb-2">Due to the nature of digital content, all purchases (coins, episodes, subscriptions, etc.) are generally non-refundable.</p>
                    <p class="mb-2 font-medium">Refunds are NOT issued when:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You changed your mind after purchase</li>
                        <li>You did not like a story/content</li>
                        <li>You purchased content by mistake</li>
                        <li>You forgot to cancel subscription</li>
                        <li>There is lack of usage of subscription</li>
                        <li>You no longer wish to use our services</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">2.2 Valid Refund Cases</h3>
                    <p class="mb-2">A refund may be considered only if:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You were charged multiple times for the same purchase</li>
                        <li>You did not receive purchased content due to technical error on our platform</li>
                        <li>There is a proven case of unauthorized payment from your account</li>
                        <li>Content is significantly different from what was described</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">2.3 How to Request a Refund</h3>
                    <p class="mb-2">To request a refund, please contact our support team with the following information:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Registered email / phone number associated with your account</li>
                        <li>Transaction ID and date of purchase</li>
                        <li>Screenshot of payment receipt</li>
                        <li>Detailed description of the issue</li>
                    </ul>
                    <p class="mb-2">Refund approval is subject to verification of your claim.</p>
                    <p>If approved, refunds are processed within 7–14 business days to the original payment method.</p>
                </div>

                <!-- Subscription Cancellation -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">3. Subscription Cancellation</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">3.1 How to Cancel</h3>
                    <p class="mb-2">You may cancel your subscription at any time through your account settings.</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Cancellation stops future auto-renewals</li>
                        <li>Cancellation does not provide a refund for the existing subscription cycle</li>
                        <li>Access to subscription benefits remains until the current paid period expires</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">3.2 Platform-Specific Cancellations</h3>
                    <p class="mb-2">Subscriptions purchased through third-party platforms must be cancelled through those platforms:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Play Store / App Store subscriptions must be cancelled through the respective store settings</li>
                        <li>Web subscriptions can be cancelled directly through your Funtel Stories account</li>
                    </ul>
                </div>

                <!-- In-App Purchases -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">4. In-App Purchases (Coins / Credits)</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.1 Nature of Virtual Currency</h3>
                    <p class="mb-2">Coins / credits purchased inside the app:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Are non-refundable once purchased</li>
                        <li>Do not have monetary value outside the app</li>
                        <li>Cannot be transferred, exchanged, or converted to cash</li>
                        <li>Expire according to the terms specified at the time of purchase</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">4.2 Usage of Coins/Credits</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Coins/credits can only be used to access premium content within the Funtel Stories platform</li>
                        <li>Once used to unlock content, coins/credits cannot be refunded or returned to your account</li>
                        <li>Promotional or bonus coins/credits may have additional restrictions on usage and expiration</li>
                    </ul>
                </div>

                <!-- Content Access Issues -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">5. Content Access Issues</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.1 Troubleshooting Steps</h3>
                    <p class="mb-2">If you face issues accessing purchased content, please try these steps first:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Restart the Funtel Stories application</li>
                        <li>Re-login to your account</li>
                        <li>Ensure you have a stable internet connection</li>
                        <li>Update to the latest version of the app</li>
                        <li>Check your purchase history to confirm the transaction</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">5.2 Technical Support</h3>
                    <p class="mb-2">If the issue continues after trying the above steps, contact our support team with:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Your account details (username/email)</li>
                        <li>Name of the content you're trying to access</li>
                        <li>Date and time of purchase</li>
                        <li>Screenshots of any error messages</li>
                    </ul>
                </div>

                <!-- Unauthorized Transactions -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">6. Unauthorized Transactions</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">6.1 Reporting Unauthorized Charges</h3>
                    <p class="mb-2">If you notice an unauthorized transaction on your account:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Inform us within 72 hours of discovering the unauthorized charge</li>
                        <li>Provide proof of transaction and any relevant details</li>
                        <li>Our team will investigate and take appropriate action</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">6.2 Account Security</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>You are responsible for maintaining the security of your account credentials</li>
                        <li>We recommend using strong, unique passwords and enabling two-factor authentication if available</li>
                        <li>Notify us immediately if you suspect your account has been compromised</li>
                    </ul>
                </div>

                <!-- Chargebacks -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">7. Chargebacks</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">7.1 Policy on Chargebacks</h3>
                    <p class="mb-2">If you contact your bank/payment provider for a chargeback without first contacting us:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Your Funtel Stories account may be temporarily or permanently suspended</li>
                        <li>Any remaining coins, credits, or subscription benefits may be forfeited</li>
                        <li>Future purchases may be restricted</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">7.2 Resolution Process</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>We encourage discussing any billing issues with our support team first</li>
                        <li>Most issues can be resolved directly without involving payment providers</li>
                        <li>If a chargeback is initiated, we will provide the necessary documentation to the payment processor</li>
                    </ul>
                </div>

                <!-- Contact Information -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50" id="contact">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">8. Contact Us</h2>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">8.1 Support Channels</h3>
                    <p class="mb-2">For any refund or billing-related queries, contact our support team:</p>
                    
                    <div class="bg-wattpad-primary/10 p-4 rounded-lg mb-4">
                        <h3 class="font-bold mb-2">Customer Support</h3>
                        <p class="mb-1">📧 Email: support&#64;funtelstudio.com</p>
                        <p class="mb-1">🌐 Website: https://stories.funtelstudio.com/</p>
                        <p>⏰ Support Hours: 10 AM – 7 PM IST (Monday–Saturday)</p>
                    </div>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">8.2 Information to Include</h3>
                    <p class="mb-2">To help us resolve your issue quickly, please include the following in your communication:</p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Your registered email address or phone number</li>
                        <li>Username on Funtel Stories</li>
                        <li>Detailed description of the issue</li>
                        <li>Relevant transaction IDs, dates, and amounts</li>
                        <li>Screenshots of error messages or purchase confirmations</li>
                    </ul>
                </div>

                <!-- Policy Note -->
                <div class="terms-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">Note</h2>
                    <div class="bg-wattpad-accent/10 border border-wattpad-accent/20 rounded-xl p-4">
                        <p class="mb-2">
                            By purchasing or subscribing on Funtel Stories, you acknowledge that you have read, understood, and agree to this Refund & Cancellation Policy.
                        </p>
                        <p>
                            Funtel Stories reserves the right to modify this policy at any time. Any changes will be effective immediately upon posting the updated policy on our platform.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </main>